import { ArrowDown, ArrowRight, Check, ContactRound, RefreshCw, ScanLine, ShieldCheck, Sparkles } from 'lucide-react'
import { DigitalCardView } from '../components/DigitalCardView'
import { PageShell } from '../components/SiteChrome'
import type { DigitalCard } from '../types/card'

const playStoreUrl = 'https://play.google.com/store/apps/details?id=com.umdaventures.sandhi'

const demoCard: DigitalCard = {
  id: 'demo', user_id: 'demo', name: 'Mira Kapoor', email: 'hello@mirakapoor.co', phone_number: '98765 43210', country_code: '+91',
  profession: 'Brand Strategist', job_title: 'Independent Brand Strategist', company: 'Fieldwork Studio',
  introduction: 'I help ambitious, human businesses find the clear idea that makes everything click.',
  website_url: 'mirakapoor.co', full_address: 'Mumbai, India', linkedin_url: 'https://linkedin.com', instagram_url: 'https://instagram.com',
  card_style: { backgroundColor: '#101b34', textColor: '#f7f5ef', accentColor: '#79f2c0', layout: 'creative', borderRadius: 28, usePatternBackground: true, patternType: 'dots' },
  created_at: new Date().toISOString(), updated_at: new Date().toISOString(), is_active: true, is_public: true,
}

const faq = [
  ['Do people need the app to view my card?', 'No. Every shared Sandhi Card opens as a fast, responsive web page. Anyone can call, email, visit your links, or save your contact without installing anything.'],
  ['What happens when my details change?', 'Update your card once in the app. The same link and QR code immediately show your latest information, so old connections never become outdated.'],
  ['Can I control who sees my information?', 'Yes. You decide whether a card is public and what details go on it. Private or inactive cards are not shown on the public website.'],
  ['Can someone save my details to their phone?', 'Yes. The “Save contact” button downloads a standard vCard that works with Android, iPhone, Outlook, and most contact apps.'],
]

export function HomePage() {
  return (
    <PageShell>
      <main>
        <section className="hero" id="top">
          <div className="hero__copy">
            <div className="hero__note"><span />A better first impression</div>
            <h1>Your best introduction,<br /><em>ready to share.</em></h1>
            <p>Sandhi turns your professional identity into one elegant digital card—always current, genuinely useful, and ready whenever opportunity calls.</p>
            <div className="hero__actions">
              <a className="button button--primary" href={playStoreUrl} target="_blank" rel="noreferrer">Create your card <ArrowRight size={18} /></a>
              <a className="text-link" href="#how-it-works">See how it works <ArrowDown size={16} /></a>
            </div>
            <div className="hero__proof">
              <span><Check size={14} /> No paper</span><span><Check size={14} /> No app needed to view</span><span><Check size={14} /> Always up to date</span>
            </div>
          </div>
          <div className="hero__product" aria-label="Example Sandhi digital card">
            <div className="product-caption"><span>01</span><p>This is what your<br />new contact sees.</p></div>
            <div className="hero__card-wrap"><DigitalCardView card={demoCard} interactive={false} /></div>
            <div className="scan-chip"><ScanLine size={18} /><span><b>One scan.</b><br />Everything they need.</span></div>
          </div>
        </section>

        <section className="statement-strip" aria-label="Product promise">
          <span>Meet.</span><i>•</i><span>Share.</span><i>•</i><span>Stay connected.</span><i>•</i><span>Never reprint.</span>
        </section>

        <section className="how" id="how-it-works">
          <div className="section-heading">
            <p>How Sandhi works</p><h2>From hello to saved contact<br />in less than a minute.</h2>
          </div>
          <div className="steps">
            <article><div className="step-number">01</div><Sparkles /><h3>Make it yours</h3><p>Add the details that matter, choose a look that feels like you, and publish when you’re ready.</p></article>
            <article><div className="step-number">02</div><ScanLine /><h3>Share in the moment</h3><p>Show your QR code or send your link. It opens cleanly on any modern phone or computer.</p></article>
            <article><div className="step-number">03</div><ContactRound /><h3>Turn meetings into contacts</h3><p>People can call, message, explore your work, and save your full contact in a single tap.</p></article>
          </div>
        </section>

        <section className="feature-story" id="features">
          <div className="feature-story__visual">
            <div className="feature-orbit feature-orbit--one" />
            <div className="feature-orbit feature-orbit--two" />
            <div className="mini-profile"><span>MK</span><div><b>Mira Kapoor</b><small>Updated just now</small></div><RefreshCw size={17} /></div>
            <div className="live-pill"><span /> LIVE CARD</div>
            <blockquote>“My title changed.<br />My card changed<br />everywhere.”</blockquote>
          </div>
          <div className="feature-story__copy">
            <p className="section-label">Built for real networking</p>
            <h2>Your card keeps working<br />after the meeting ends.</h2>
            <div className="feature-list">
              <article><RefreshCw /><div><h3>One update. Every link.</h3><p>Change your number, role, website, or profile image once. Every shared card reflects it.</p></div></article>
              <article><ShieldCheck /><div><h3>You decide what travels.</h3><p>Make a card public only when it is ready to share. Inactive and private cards stay unavailable.</p></div></article>
              <article><ContactRound /><div><h3>Designed for the next action.</h3><p>Call, email, WhatsApp, maps, portfolio, socials, and save-to-contacts are all one tap away.</p></div></article>
            </div>
          </div>
        </section>

        <section className="use-cases">
          <div className="section-heading section-heading--row"><div><p>Made to move with you</p><h2>One identity.<br />Every room.</h2></div><p className="section-intro">At a conference, across a counter, on a call, or in a message—Sandhi makes the handoff feel considered.</p></div>
          <div className="use-case-grid">
            <article className="use-case use-case--large"><span>01 / FOUNDERS</span><h3>Make the idea memorable,<br />not the paper.</h3><p>Keep your pitch, company, and ways to reach you in one polished place.</p></article>
            <article className="use-case use-case--blue"><span>02 / INDEPENDENTS</span><h3>Your work and your contact, together.</h3><p>Lead people from introduction to portfolio without losing momentum.</p></article>
            <article className="use-case use-case--light"><span>03 / TEAMS</span><h3>A consistent way to show up.</h3><p>Give every client-facing conversation a clear, modern close.</p></article>
          </div>
        </section>

        <section className="faq" id="faq">
          <div className="section-heading"><p>Good to know</p><h2>Questions, answered.</h2></div>
          <div className="faq-list">
            {faq.map(([question, answer], index) => <details key={question} open={index === 0}><summary><span>{String(index + 1).padStart(2, '0')}</span>{question}<b>+</b></summary><p>{answer}</p></details>)}
          </div>
        </section>

        <section className="final-cta">
          <div className="final-cta__mark">S</div>
          <p>The next introduction<br />could change everything.</p>
          <h2>Be ready for it.</h2>
          <a className="button button--light" href={playStoreUrl} target="_blank" rel="noreferrer">Get Sandhi Card <ArrowRight size={18} /></a>
          <div className="final-cta__legal">Available on Google Play · Free to get started</div>
        </section>
      </main>
    </PageShell>
  )
}
